"use client";

import {
	useCallback,
	useLayoutEffect,
	useMemo,
	useState,
	type ReactNode
} from "react";
import type { InfiniteHitsCache } from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import {
	Configure,
	InstantSearch,
	useClearRefinements,
	useInfiniteHits,
	useInstantSearch,
	useRange,
	useRefinementList,
	useSortBy,
	useToggleRefinement
} from "react-instantsearch";

import { searchCategoryListing } from "@/app/actions/category-listing";
import type { ProductCardViewModel } from "@/app/components/shared/product-card";
import { RANGE_MIN, type FilterRange } from "@/composites/product-listing";
import {
	LISTING_PAGE_SIZE,
	LISTING_STEP_BELOW_LG
} from "@/domain/product-listing/listing-depth";
import {
	LISTING_TOGGLE_VALUES,
	listingFilterOperator,
	type ListingFacetAttribute
} from "@/domain/product-listing/listing-facets";

import type { ListingFilterDefinition } from "../product-listing-content";
import type { CategoryListing } from "../get-product-listing-page-data";
import {
	NO_SELECTION,
	toFilterGroups,
	type FacetCounts,
	type FacetSelection,
	type FacetStats,
	type RangeFormat
} from "../listing-filters";
import {
	createListingRouting,
	createListingSearchClient,
	LISTING_FACET_LIMIT,
	LISTING_INDEX,
	LISTING_SORT_OPTIONS,
	toPageHref
} from "../listing-search";
import { createListingHitsCache, type ServerPages } from "./listing-hits-cache";
import { initialShownBelowLg, useShownBelowLg } from "./shown-below-lg";
import {
	LG_UP,
	ListingStateContext,
	type ListingOnward,
	type ListingStateValue
} from "./listing-state-context";
import { hasRefinements, listingStatus } from "./listing-status";

type ListingHit = ProductCardViewModel & { objectID: string };

const CURRENCY_BY_LOCALE: Record<string, string> = {
	"en-US": "USD",
	"en-CA": "CAD"
};

function toRangeFormat(locale: string): RangeFormat {
	return {
		locale,
		formatOptions: {
			style: "currency",
			currency: CURRENCY_BY_LOCALE[locale] ?? "USD",
			maximumFractionDigits: 0
		}
	};
}

export interface ListingStateProviderProps {
	superCategoryCode: string;
	categoryCode: string;
	/** The listing's own address, without a query. */
	href: string;
	locale: string;
	initial: CategoryListing;
	onward: ListingOnward;
	children: ReactNode;
}

export function ListingStateProvider({
	superCategoryCode,
	categoryCode,
	href,
	locale,
	initial,
	onward,
	children
}: ListingStateProviderProps): JSX.Element {
	const searchClient = useMemo(
		() =>
			createListingSearchClient((searches) =>
				searchCategoryListing({
					superCategoryCode,
					categoryCode,
					locale,
					searches
				})
			),
		[superCategoryCode, categoryCode, locale]
	);
	const attributes = useMemo(
		() => [...new Set(initial.filters.map((filter) => filter.attribute))],
		[initial.filters]
	);
	const routing = useMemo(
		() => createListingRouting(attributes),
		[attributes]
	);
	const rangeFormat = useMemo(() => toRangeFormat(locale), [locale]);
	const cache = useMemo(
		() =>
			createListingHitsCache<ListingHit>(
				`plp-listing:${locale}:${categoryCode}`,
				toServerPages(initial)
			),
		[locale, categoryCode, initial]
	);
	// InstantSearch renders nothing before it starts, so show the server's page.
	// It comes first so the browser's scroll anchor sits above the live copy
	// while both are briefly mounted, and Back keeps the shopper's place.
	const [searchStarted, setSearchStarted] = useState(false);
	const markStarted = useCallback(() => setSearchStarted(true), []);

	return (
		<>
			{!searchStarted && (
				<ListingStateContext.Provider
					value={toServerState(initial, rangeFormat, onward, href)}
				>
					{children}
				</ListingStateContext.Provider>
			)}
			<InstantSearch
				searchClient={searchClient}
				indexName={LISTING_INDEX}
				routing={routing}
				future={{ preserveSharedStateOnUnmount: true }}
			>
				<Configure hitsPerPage={LISTING_PAGE_SIZE} />
				<ListingWidgets filters={initial.filters} />
				<ListingFacets
					initial={initial}
					href={href}
					onward={onward}
					cache={cache}
					rangeFormat={rangeFormat}
					onStart={markStarted}
				>
					{children}
				</ListingFacets>
			</InstantSearch>
		</>
	);
}

function noop(): void {}

function firstPositionOf(initial: CategoryListing): number {
	return ((initial.firstPage ?? 1) - 1) * LISTING_PAGE_SIZE + 1;
}

function toServerPages(initial: CategoryListing): ServerPages<ListingHit> {
	const firstPosition = firstPositionOf(initial);
	const hits = initial.cards.map((card, index) => ({
		...card,
		objectID: card.product.sku,
		__position: firstPosition + index
	}));
	const pages: (typeof hits)[] = [];
	for (let start = 0; start < hits.length; start += LISTING_PAGE_SIZE) {
		pages.push(hits.slice(start, start + LISTING_PAGE_SIZE));
	}
	const first = (initial.firstPage ?? 1) - 1;
	return {
		first,
		pages,
		complete: first === 0 && hits.length >= initial.total
	};
}

function toServerState(
	initial: CategoryListing,
	rangeFormat: RangeFormat,
	onward: ListingOnward,
	href: string
): ListingStateValue {
	return {
		groups: toFilterGroups(
			initial.filters,
			{
				total: initial.total,
				counts: initial.facets,
				stats: initial.stats
			},
			NO_SELECTION,
			rangeFormat
		),
		cards: initial.cards,
		firstPosition: firstPositionOf(initial),
		total: initial.total,
		isPending: false,
		sort: LISTING_INDEX,
		toggle: noop,
		setRange: noop,
		setSort: noop,
		clearAll: noop,
		isLoadingMore: false,
		hasPrevious: (initial.firstPage ?? 1) > 1,
		isLoadingPrevious: false,
		pageHref: (page) => toPageHref(href, undefined, [], page),
		shownBelowLg: Number.POSITIVE_INFINITY,
		newCardFocused: noop,
		status: listingStatus({
			count: initial.cards.length,
			filtered: false,
			failed: false
		}),
		retry: noop,
		onward
	};
}

function ListingWidgets({
	filters
}: {
	filters: ListingFilterDefinition[];
}): JSX.Element {
	const attributes = new Map(
		filters.map((filter) => [filter.attribute, filter.kind])
	);
	return (
		<>
			{[...attributes].map(([attribute, kind]) => {
				switch (kind) {
					case "list":
						return (
							<ListWidget key={attribute} attribute={attribute} />
						);
					case "toggle":
						return (
							<ToggleWidget
								key={attribute}
								attribute={attribute}
							/>
						);
					case "range":
						return (
							<RangeWidget
								key={attribute}
								attribute={attribute}
							/>
						);
				}
			})}
			<SortWidget />
		</>
	);
}

interface WidgetProps {
	attribute: ListingFacetAttribute;
}

function ListWidget({ attribute }: WidgetProps): null {
	useRefinementList({
		attribute,
		limit: LISTING_FACET_LIMIT,
		operator: listingFilterOperator(attribute)
	});
	return null;
}

function ToggleWidget({ attribute }: WidgetProps): null {
	useToggleRefinement({
		attribute,
		on: [...(LISTING_TOGGLE_VALUES[attribute] ?? [])]
	});
	return null;
}

function RangeWidget({ attribute }: WidgetProps): null {
	useRange({ attribute });
	return null;
}

function SortWidget(): null {
	useSortBy({ items: LISTING_SORT_OPTIONS });
	return null;
}

function parseRange(value: string | undefined): FilterRange {
	const [min, max] = (value ?? "")
		.split(":")
		.map((part) => (part === "" ? undefined : Number(part)));
	return {
		min: Number.isFinite(min) ? min : undefined,
		max: Number.isFinite(max) ? max : undefined
	};
}

function ListingFacets({
	initial,
	href,
	onward,
	cache,
	rangeFormat,
	onStart,
	children
}: {
	initial: CategoryListing;
	href: string;
	onward: ListingOnward;
	cache: InfiniteHitsCache<ListingHit>;
	rangeFormat: RangeFormat;
	onStart: () => void;
	children: ReactNode;
}): JSX.Element {
	useLayoutEffect(onStart, [onStart]);

	const {
		items: hits,
		showMore,
		showPrevious,
		isFirstPage
	} = useInfiniteHits<ListingHit>({ cache });
	const { refine: clearAll } = useClearRefinements();
	const { results, status, indexUiState, setIndexUiState, refresh } =
		useInstantSearch({ catchError: true });
	const hasResults = !results.__isArtificial;
	const filtered = hasRefinements(indexUiState);
	// A failed refresh of the server's own page keeps its products; a failed filter or sort shows the error.
	const failed = status === "error" && (hasResults || filtered);
	const isPending = status === "loading" || status === "stalled";
	const firstPosition = hits[0]?.__position ?? 1;

	const attributes = [
		...new Set(initial.filters.map((filter) => filter.attribute))
	];
	const [shownBelowLg, setShownBelowLg] = useShownBelowLg(
		`plp-shown:${href}${toPageHref("", indexUiState, attributes, 1)}`,
		initialShownBelowLg(initial.firstPage ?? 1, initial.cards.length)
	);

	const [loadMoreFrom, setLoadMoreFrom] = useState<number>();
	const loadMore = useCallback(() => {
		if (window.matchMedia(LG_UP).matches) {
			setShownBelowLg(Number.POSITIVE_INFINITY);
			setLoadMoreFrom(hits.length);
			showMore();
			return;
		}
		const showing = Math.min(shownBelowLg, hits.length);
		const next = showing + LISTING_STEP_BELOW_LG;
		setShownBelowLg(next);
		setLoadMoreFrom(showing);
		if (next > hits.length) showMore();
	}, [hits.length, showMore, shownBelowLg, setShownBelowLg]);
	const newCardFocused = useCallback(() => setLoadMoreFrom(undefined), []);
	const [previousFrom, setPreviousFrom] = useState<number>();
	const loadPrevious = useCallback(() => {
		setPreviousFrom(firstPosition);
		showPrevious();
	}, [firstPosition, showPrevious]);

	const counts: FacetCounts = {};
	const stats: FacetStats = {};
	for (const filter of initial.filters) {
		const values = results.getFacetValues(filter.attribute, {}) as
			{ name: string; count: number }[] | undefined;
		counts[filter.attribute] = Object.fromEntries(
			(Array.isArray(values) ? values : []).map((value) => [
				value.name,
				value.count
			])
		);
		const facetStats = results.getFacetStats(filter.attribute);
		if (facetStats) stats[filter.attribute] = facetStats;
	}

	const selection: FacetSelection = {
		lists: indexUiState.refinementList ?? {},
		toggles: indexUiState.toggle ?? {},
		ranges: Object.fromEntries(
			Object.entries(indexUiState.range ?? {}).map(
				([attribute, value]) => [attribute, parseRange(value)]
			)
		)
	};

	function filterById(groupId: string): ListingFilterDefinition | undefined {
		return initial.filters.find((filter) => filter.id === groupId);
	}

	function toggle(groupId: string, value: string): void {
		const filter = filterById(groupId);
		if (!filter) return;
		const { attribute } = filter;
		setIndexUiState((state) => {
			if (filter.kind === "toggle") {
				return {
					...state,
					page: undefined,
					toggle: {
						...state.toggle,
						[attribute]: !state.toggle?.[attribute]
					}
				};
			}
			if (filter.kind === "range") {
				const [min = "", max = ""] = (
					state.range?.[attribute] ?? ""
				).split(":");
				const kept = value === RANGE_MIN ? `:${max}` : `${min}:`;
				return {
					...state,
					page: undefined,
					range: {
						...state.range,
						[attribute]: kept === ":" ? "" : kept
					}
				};
			}
			const current = state.refinementList?.[attribute] ?? [];
			const next = current.includes(value)
				? current.filter((entry) => entry !== value)
				: [...current, value];
			return {
				...state,
				page: undefined,
				refinementList: { ...state.refinementList, [attribute]: next }
			};
		});
	}

	function setRange(groupId: string, range: FilterRange): void {
		const filter = filterById(groupId);
		if (!filter) return;
		const value = `${range.min ?? ""}:${range.max ?? ""}`;
		setIndexUiState((state) => ({
			...state,
			page: undefined,
			range: {
				...state.range,
				[filter.attribute]: value === ":" ? "" : value
			}
		}));
	}

	function setSort(sort: string): void {
		setIndexUiState((state) => ({
			...state,
			page: undefined,
			sortBy: sort
		}));
	}

	const handlers = {
		toggle,
		setRange,
		setSort,
		clearAll,
		loadMore,
		loadPrevious,
		newCardFocused,
		retry: refresh,
		onward,
		pageHref: (page: number) =>
			toPageHref(href, indexUiState, attributes, page),
		shownBelowLg
	};
	const shown = hasResults
		? {
				groups: toFilterGroups(
					initial.filters,
					{ total: results.nbHits, counts, stats },
					selection,
					rangeFormat,
					initial.facets
				),
				cards: hits,
				firstPosition,
				total: results.nbHits,
				isPending,
				sort: indexUiState.sortBy ?? LISTING_INDEX,
				...handlers,
				isLoadingMore: isPending && loadMoreFrom === hits.length,
				hasPrevious: !isFirstPage,
				isLoadingPrevious: isPending && previousFrom === firstPosition,
				firstNewCard:
					loadMoreFrom !== undefined && hits.length > loadMoreFrom
						? loadMoreFrom
						: undefined
			}
		: {
				...toServerState(initial, rangeFormat, onward, href),
				...handlers
			};
	const value: ListingStateValue = {
		...shown,
		status: listingStatus({ count: shown.cards.length, filtered, failed })
	};

	return (
		<ListingStateContext.Provider value={value}>
			{children}
		</ListingStateContext.Provider>
	);
}
